import React, {ChangeEvent} from 'react'
import {useIntl} from 'react-intl'
import {validatePhoneNumber} from '../../utils/phone-utils'
import {
  MAX_FIELD_LENGTH,
  MAX_ADDRESS_NAME_LENGTH,
  TITLE_OPTIONS,
  POSTCODE_REGEXP,
  ADDRESS_REGEXP,
  FIRST_NAME_REGEXP,
  LAST_NAME_REGEXP,
} from '../../utils/constants'
import {Box, Text} from '../../vanilla'
import {validateEmail} from '../../utils/email-utils'
import {AddressFormFields, CreateFields} from '../../types/forms'
import {ControllerRenderProps} from 'react-hook-form'
import {formatPostcode} from './useCheckPostcodeFields'
import {FieldInputProps} from '../../types/forms'

export const useAddressFields: CreateFields<AddressFormFields> = ({
  form: {
    control,
    formState: {errors},
    setValue,
  },
  withAddressHelpText = true,
  pathname,
}) => {
  const {formatMessage} = useIntl()

  const fields = {
    title: {
      name: `title`,
      label: formatMessage({defaultMessage: 'Title'}),
      defaultValue: '',
      type: 'select' as const,
      options: TITLE_OPTIONS.map(({label, value}) => ({
        label,
        value,
      })),
      inputProps: {
        onChange: (e: ChangeEvent<HTMLSelectElement>) => setValue(`title`, e.target.value),
        showAsterisk: true,
        'data-test-selector': 'title-field'
      } as FieldInputProps,
      rules: {required: formatMessage({defaultMessage: 'This field is required.'})},
      error: errors[`title`],
      control,
    },
    firstName: {
      name: `firstName`,
      label: formatMessage({defaultMessage: 'First Name'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        pattern: {
          value: FIRST_NAME_REGEXP,
          message: 'Please enter a valid value',
        },
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `First Name cannot be longer than ${MAX_FIELD_LENGTH} characters`,
        },
      },
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'first-name-field'
      },
      error: errors[`firstName`],
      control,
    },
    lastName: {
      name: `lastName`,
      label: formatMessage({defaultMessage: 'Last Name'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        pattern: {
          value: LAST_NAME_REGEXP,
          message: 'Please enter a valid value',
        },
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `Last Name cannot be longer than ${MAX_FIELD_LENGTH} characters`,
        },
      },
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'last-name-field'
      },
      error: errors[`lastName`],
      control,
    },
    addressId: {
      name: `addressId`,
      label: formatMessage({defaultMessage: 'Name this address'}),
      defaultValue: '',
      type: 'text' as const,
      rules: {
        maxLength: {
          value: MAX_ADDRESS_NAME_LENGTH,
          message: `Address name cannot be longer than ${MAX_ADDRESS_NAME_LENGTH} characters.`,
        },

        pattern: {
          value: ADDRESS_REGEXP,
          message: 'Please enter a valid value',
        },
      },
      error: errors[`addressId`],
      control,
      inputProps: {
        'data-test-selector': 'address-name-field',
        labelDataTestSelector: 'address-name-label'
      },
    },
    phone: {
      name: `phone`,
      label: formatMessage({defaultMessage: 'Contact number'}),
      defaultValue: '',
      type: 'tel' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        validate: (phone: string) =>
          validatePhoneNumber(phone).isValid ||
          formatMessage({defaultMessage: 'Please specify a valid phone number.'}),
      },
      error: errors[`phone`],
      inputProps: {
        inputMode: 'numeric' as const,
        showAsterisk: true,
        'data-test-selector': 'phone-field'
      },
      control,
      helpText: (
        <Box marginLeft="8px">
          <Text variant="text2" lineHeight="none" color="gray800">
            We&apos;ll use this to contact you about your order
          </Text>
        </Box>
      ),
    },
    address1: {
      name: `address1`,
      label: formatMessage({defaultMessage: 'Address line 1'}),
      defaultValue: '',
      type: 'text' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        pattern: {
          value: ADDRESS_REGEXP,
          message: 'Please enter a valid value',
        },
      },
      error: errors[`address1`],
      control,
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'address-line-1-field'
      },
      helpText: withAddressHelpText ? (
        <Box marginLeft="8px">
          <Text variant="text2" lineHeight="none" color="textMuted">
            Street Address, P.O. Box
          </Text>
        </Box>
      ) : null,
    },
    address2: {
      name: `address2`,
      label: formatMessage({defaultMessage: 'Address line 2'}),
      rules: {
        pattern: {
          value: ADDRESS_REGEXP,
          message: formatMessage({defaultMessage: 'Please enter a valid value'}),
        },
      },
      defaultValue: '',
      type: 'text' as const,
      error: errors[`address2`],
      control,
      inputProps: {
        'data-test-selector': 'address-line-2-field'
      },
      helpText: withAddressHelpText ? (
        <Box marginLeft="8px">
          <Text variant="text2" lineHeight="none" color="textMuted">
            Apartment, Suite, Unit, Floor
          </Text>
        </Box>
      ) : null,
    },
    city: {
      name: `city`,
      label: formatMessage({defaultMessage: 'Town or City'}),
      defaultValue: '',
      type: 'text' as const,
      rules: {
        pattern: {
          value: ADDRESS_REGEXP,
          message: formatMessage({defaultMessage: 'Please enter a valid value'}),
        },
        required: formatMessage({defaultMessage: 'This field is required.'}),
      },
      error: errors[`city`],
      inputProps: {
        showAsterisk: true,
        'data-test-selector': 'address-city-field'
      },
      control,
    },
    stateCode: {
      name: `stateCode`,
      label: formatMessage({defaultMessage: 'County'}),
      rules: {
        pattern: {
          value: ADDRESS_REGEXP,
          message: formatMessage({defaultMessage: 'Please enter a valid value'}),
        },
      },
      defaultValue: '',
      type: 'text' as const,
      error: errors[`stateCode`],
      control,
      inputProps: {
        'data-test-selector': 'address-county-field'
      },
    },
    countryCode: {
      name: `countryCode`,
      label: formatMessage({defaultMessage: 'Country'}),
      defaultValue: 'GB',
      type: 'hidden' as const,
      error: errors[`countryCode`],
      control,
    },
    postcode: {
      name: `postalCode`,
      label: formatMessage({defaultMessage: 'Postcode'}),
      type: 'text' as const,
      defaultValue: '',
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        pattern: {
          value: POSTCODE_REGEXP,
          message: 'Please specify a valid postal code',
        },
      },
      inputProps: ({onChange, onBlur}: Pick<ControllerRenderProps, 'onChange' | 'onBlur'>) => ({
        id: 'registration-postcode-field',
        'data-test-selector': 'address-postcode-field',
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          const formattedValue = formatPostcode(e.target.value)
          onChange(formattedValue)
          onBlur()
        },
      }),
      error: errors[`postalCode`],
      control,
    },
    preferred: {
      name: `preferred`,
      label: (pathname === '/checkout/summary' || pathname === '/pre-book-delivery/payment') ? 'Save new address to My Addresses' : 'Primary',
      defaultValue: false,
      type: 'checkbox' as const,
      rules: {},
      control,
      inputProps: {
        'data-test-selector': 'address-preferred-checkbox'
      },
    },
    email: {
      name: `email`,
      label: formatMessage({defaultMessage: 'Email'}),
      defaultValue: '',
      type: 'email' as const,
      rules: {
        required: formatMessage({defaultMessage: 'This field is required.'}),
        maxLength: {
          value: MAX_FIELD_LENGTH,
          message: `Email cannot be longer than ${MAX_FIELD_LENGTH} characters.`,
        },
        validate: (value: string) =>
          validateEmail(value).isValid ||
          formatMessage({defaultMessage: 'Please enter a valid email address.'}),
      },
      inputProps: {
        // For login fields browser still tries to autofill even if autocomplete is off... So setting `new-password` to fix it.
        // See: https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
        autoComplete: 'new-password',
        maxLength: MAX_FIELD_LENGTH,
        showAsterisk: true,
        'data-test-selector': 'email-field'
      },
      error: errors[`email`],
      control,
    },
  }

  return fields
}
